/**
 * KUNDERNA — firmans kundregister, och det enda stället ROT-SATSEN går att sätta
 * på en kund som redan finns.
 *
 * VARFÖR DEN MÅSTE FINNAS. schema/company.sql rad 525-526: time_entry
 * .rot_hundredths "fryses fran customer.rot_hundredths och ar 0 om kunden saknar
 * ROT". Momentets rot_eligible säger VAD som ger ROT; kundens rot_hundredths
 * säger MED HUR MYCKET. Utan den här skärmen kunde satsen bara sättas i samma
 * andetag som en ny kund skapades (NyOrder.tsx) — en kund som redan låg i
 * registret var permanent utan ROT, ROT-kolumnen i CSV:n skrev alltid '0', och
 * ROT-krysset på momentskärmen var utan verkan på fakturaunderlaget hur många
 * moment som än var märkta.
 *
 * SATSEN GÄLLER FRAMÅT, ALDRIG BAKÅT. Tidraden fryser sin sats när den sparas,
 * så en kund som får ROT i dag ger ROT på det som rapporteras HÄREFTER. Det står
 * på skärmen, för det är precis den sortens sak som annars upptäcks först när
 * fakturan är skickad.
 *
 * ROT BARA PÅ PRIVATKUND. CHECK (rot_hundredths IS NULL OR (kind = 'private'
 * AND rot_hundredths BETWEEN 0 AND 10000)) i schemat — fältet finns därför inte
 * ens för en företagskund, och byter chefen kundslag till 'company' nollas
 * satsen i samma skrivning i stället för att servern ska svara VALIDATION_FAILED.
 *
 * BARA CHEFEN SKRIVER. 'put:customer' är MANAGER_ONLY i SYNC_RULES och
 * bokföraren står inte i en enda rad där (beslut 07) trots att han har full vy.
 * Han får listan — han stämmer av fakturaunderlag mot bokföringen — men inga
 * fält, precis som i NyOrder.tsx och Moment.tsx.
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09). När Spara trycks fryses en
 * PLAN — ett op_id och en kropp — och fälten låses. En omsändning skickar SAMMA
 * op_id med SAMMA kropp och får därför 'duplicate' i stället för en andra
 * skrivning.
 *
 * INGEN RADERING. 'delete:customer' finns i kontraktet, men en kund med levande
 * arbetsordrar går inte att radera (ROW_IN_USE) och en kund utan dem är
 * ofarlig att låta ligga. Knappen är därför inte byggd — se LUCKOR.
 */

import { useState } from 'react';
import type { CustomerKind, CustomerRow, CustomerWrite, SyncOp } from '@delat/types';
import {
  FAKTOR,
  Falt,
  Felruta,
  Knapp,
  Laddar,
  Listrad,
  Talfalt,
  Tomtext,
  Valfalt,
  Yta,
  tillHeltal,
} from '../ui';
import { granska } from '../api';
import type { Klartidfel } from '../api';
import {
  MAX_ADRESS_TECKEN,
  MAX_KUNDNAMN_TECKEN,
  MAX_ROT_HUNDRADELAR,
} from '../api/konstanter';
import { useInloggad } from '../app/session';
import { glomAllaFirmaminnen } from '../app/firmaminnen';
import {
  andradKundOp,
  arKlar,
  forstaMisslyckade,
  nyttOpId,
  skickaOps,
  useArbetsorderlager,
} from './arbetsorder-data';

/** 3000 -> "30". 1250 -> "12,50". Ingen avrundning, någonsin. */
const procenttext = (hundradelar: number): string =>
  hundradelar % 100 === 0
    ? String(hundradelar / 100)
    : (hundradelar / 100).toFixed(2).replace('.', ',');

/** Vad som står i högerkanten på kundraden. Ingen ROT sägs rakt ut. */
const rotmarke = (kund: CustomerRow): string =>
  kund.kind === 'company'
    ? 'företag'
    : kund.rot_hundredths === null
      ? 'ingen ROT'
      : `ROT ${procenttext(Number(kund.rot_hundredths))} %`;

/** Den frysta inlämningen. Se OP_ID i filhuvudet. */
interface Plan {
  readonly namn: string;
  readonly op: SyncOp;
}

export const Kunder = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const { lage, laddaOm } = useArbetsorderlager(firmaId);

  const [valdId, sattValdId] = useState<string | null>(null);
  const [namn, sattNamn] = useState('');
  const [kundslag, sattKundslag] = useState<CustomerKind>('private');
  const [orgnr, sattOrgnr] = useState('');
  const [adress, sattAdress] = useState('');
  const [rot, sattRot] = useState('');

  const [plan, sattPlan] = useState<Plan | null>(null);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const lager = lage.lager;
  const kunder = lager.fullaKunder
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  const vald = kunder.find((k) => k.id === valdId) ?? null;
  const last = plan !== null;
  const utanRot = kunder.filter((k) => k.kind === 'private' && k.rot_hundredths === null);

  const oppna = (kund: CustomerRow): void => {
    sattFel(null);
    sattFaltfel({});
    sattPlan(null);
    if (kund.id === valdId) {
      sattValdId(null);
      return;
    }
    sattValdId(kund.id);
    sattNamn(kund.name);
    sattKundslag(kund.kind);
    sattOrgnr(kund.org_number ?? '');
    sattAdress(kund.address ?? '');
    sattRot(kund.rot_hundredths === null ? '' : procenttext(Number(kund.rot_hundredths)));
  };

  const rensaFalt = (nyckel: string): void => {
    sattFaltfel((gamla) => {
      if (gamla[nyckel] === undefined) return gamla;
      const nya = { ...gamla };
      delete nya[nyckel];
      return nya;
    });
  };

  /** Klientens egen förkontroll. Servern gör den riktiga mot schemats CHECK. */
  const byggPlan = (kund: CustomerRow): Plan | null => {
    const brister: Record<string, string> = {};
    const rensatNamn = namn.trim();
    if (rensatNamn === '') brister['namn'] = 'Kunden behöver ett namn.';
    else if (rensatNamn.length > MAX_KUNDNAMN_TECKEN) {
      brister['namn'] = `Högst ${MAX_KUNDNAMN_TECKEN} tecken.`;
    }
    if (adress.trim().length > MAX_ADRESS_TECKEN) {
      brister['adress'] = `Högst ${MAX_ADRESS_TECKEN} tecken.`;
    }

    const siffror = orgnr.replace(/[\s-]/g, '');
    if (kundslag === 'company' && siffror !== '' && !/^\d{10}$/.test(siffror)) {
      brister['orgnr'] = 'Org.nr är tio siffror.';
    }

    // ROT-SATSEN. Tomt = ingen ROT, och den finns bara på privatkund. Byter
    // chefen till företagskund nollas satsen här i stället för att servern ska
    // svara VALIDATION_FAILED på ett CHECK användaren inte kan se.
    const rotHundradelar =
      kundslag !== 'private' || rot.trim() === ''
        ? null
        : tillHeltal(rot.trim(), FAKTOR.procent_hundradel);
    if (kundslag === 'private' && rot.trim() !== '') {
      if (rotHundradelar === null) brister['rot'] = 'Skriv satsen i procent, till exempel 30.';
      else if (rotHundradelar < 0 || rotHundradelar > MAX_ROT_HUNDRADELAR) {
        brister['rot'] = 'ROT-satsen ligger mellan 0 och 100 procent.';
      }
    }

    if (Object.keys(brister).length > 0) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    const andringar: Partial<CustomerWrite> = {
      name: rensatNamn,
      kind: kundslag,
      // CHECK (kind = 'company' OR org_number IS NULL): en privatperson lagras
      // ALDRIG med personnummer.
      org_number:
        kundslag === 'company' && siffror !== ''
          ? granska.orgnummer(siffror, 'org_number')
          : null,
      address: adress.trim() === '' ? null : adress.trim(),
      rot_hundredths:
        rotHundradelar === null ? null : granska.hundradelar(rotHundradelar, 'rot_hundredths'),
    };
    return { namn: rensatNamn, op: andradKundOp(kund, andringar, nyttOpId()) };
  };

  const skicka = (aktuell: Plan): void => {
    sattPlan(aktuell);
    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, [aktuell.op]);
      // Alltid av igen, oavsett utfall: en knapp som fastnar i "Sparar ..."
      // efter ett tappat nät är en app man startar om.
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      if (!arKlar(svar.varde, aktuell.op.op_id)) {
        const opsfel = forstaMisslyckade(svar.varde);
        if (opsfel !== null) {
          sattFel(opsfel);
          return;
        }
      }
      // 'applied' eller 'duplicate' — bägge betyder att raden står som vi ville.
      sattPlan(null);
      sattValdId(null);
      // ALLA LAGREN, inte bara mitt: en andrad ROT-sats ska synas i
      // fakturaunderlaget direkt, och tidrapportens egen cache halls i en annan
      // modul. Se app/firmaminne.ts.
      glomAllaFirmaminnen();
      laddaOm();
    })();
  };

  const spara = (kund: CustomerRow): void => {
    const aktuell = plan ?? byggPlan(kund);
    if (aktuell === null) return;
    skicka(aktuell);
  };

  return (
    <>
      {!arChef && (
        <Yta rubrik="Du ser kunderna, du ändrar dem inte">
          <p style={{ margin: 0 }}>
            Kunder, moment och prislistor skrivs av chefen. Det är samma gräns som
            i attesten. Listan står kvar här så att du kan stämma av ROT-satserna
            mot fakturaunderlaget.
          </p>
        </Yta>
      )}

      {/* ROT-LUCKAN, ÖVERST OCH I KLARTEXT. Det är den som gör att timmar
          fryses utan ROT, och den syns ingen annanstans i appen. */}
      {arChef && utanRot.length > 0 && (
        <Yta>
          <p
            style={{ margin: 0, color: 'var(--warn)' }}
          >
            <b>
              {utanRot.length === 1
                ? 'En privatkund saknar ROT-sats.'
                : `${utanRot.length} privatkunder saknar ROT-sats.`}
            </b>{' '}
            Varje timme som rapporteras på deras jobb fryses utan ROT, hur många
            moment som än är ROT-märkta. Sätt satsen nedan om jobbet ger ROT.
          </p>
        </Yta>
      )}

      <Yta rubrik="Firmans kunder" rakning={`${kunder.length} st`}>
        {kunder.map((k) => (
          <Listrad
            key={k.id}
            titel={k.name}
            under={k.id === valdId ? 'Ändras nedan' : (k.address ?? undefined)}
            varde={rotmarke(k)}
            onClick={arChef && !last ? () => oppna(k) : undefined}
          />
        ))}
        {kunder.length === 0 && (
          <Tomtext>
            Firman har inga kunder än. Den första läggs upp i samma flöde som den
            första arbetsordern — under Arbetsordrar, "Ny arbetsorder".
          </Tomtext>
        )}
        {kunder.length > 0 && arChef && (
          <Tomtext>Tryck på en kund för att ändra uppgifterna eller ROT-satsen.</Tomtext>
        )}
      </Yta>

      {lager.avkortat && (
        <Tomtext>
          Firman har fler rader än som ryms i en hämtning, och listan kan därför
          sakna kunder. Ladda om skärmen.
        </Tomtext>
      )}

      {arChef && vald !== null && (
        <Yta rubrik={`Ändra ${vald.name}`}>
          <Falt
            etikett="Kundens namn"
            varde={namn}
            onAndra={(t) => {
              rensaFalt('namn');
              sattNamn(t);
            }}
            disabled={last}
            maxLength={MAX_KUNDNAMN_TECKEN}
            placeholder="Anna Andersson eller Brf Tallmon"
            {...(faltfel['namn'] === undefined ? {} : { fel: faltfel['namn'] })}
          />
          <Valfalt
            etikett="Privat eller företag"
            varde={kundslag}
            onAndra={(v) => {
              rensaFalt('rot');
              rensaFalt('orgnr');
              sattKundslag(v);
            }}
            disabled={last}
            val={[
              { varde: 'private', etikett: 'Privatkund' },
              { varde: 'company', etikett: 'Företagskund' },
            ]}
            hjalp="Privatkund lagras aldrig med personnummer. ROT finns bara på privatkund."
          />
          {kundslag === 'company' && (
            <Falt
              etikett="Org.nr"
              varde={orgnr}
              onAndra={(t) => {
                rensaFalt('orgnr');
                sattOrgnr(t);
              }}
              disabled={last}
              inputMode="numeric"
              placeholder="5565551234"
              hjalp="Tio siffror. Kan lämnas tomt."
              {...(faltfel['orgnr'] === undefined ? {} : { fel: faltfel['orgnr'] })}
            />
          )}
          <Falt
            etikett="Kundens adress"
            varde={adress}
            onAndra={(t) => {
              rensaFalt('adress');
              sattAdress(t);
            }}
            disabled={last}
            maxLength={MAX_ADRESS_TECKEN}
            placeholder="Tallmovägen 22, 122 40 Enskede"
            hjalp="Gäller för de arbetsordrar som saknar egen adress."
            {...(faltfel['adress'] === undefined ? {} : { fel: faltfel['adress'] })}
          />
          {kundslag === 'private' && (
            <Talfalt
              etikett="ROT-sats"
              varde={rot}
              onAndra={(t) => {
                rensaFalt('rot');
                sattRot(t);
              }}
              faktor={FAKTOR.procent_hundradel}
              suffix="%"
              disabled={last}
              placeholder="30"
              hjalp="Lämna tomt om kundens jobb inte ger ROT. Klartid räknar aldrig ut avdraget — satsen följer med underlaget ut."
              {...(faltfel['rot'] === undefined ? {} : { fel: faltfel['rot'] })}
              {...(rot.trim() === ''
                ? { varning: 'Utan sats fryses varje ny timme på kundens jobb utan ROT.' }
                : {})}
            />
          )}

          {/* FRAMÅT, INTE BAKÅT — och det är hela skillnaden mot momentnamnet,
              som läses om live varje gång ett underlag byggs. */}
          <Tomtext>
            Satsen fryses på tidraden när den sparas. En ändring här gäller alltså
            det som rapporteras HÄREFTER — timmar som redan är inne behåller den
            sats de frystes med.
          </Tomtext>

          {fel !== null && <Felruta fel={fel} />}

          <Knapp
            disabled={arLast || sparar}
            onClick={() => spara(vald)}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
          >
            {sparar ? 'Sparar ...' : plan === null ? 'Spara kunden' : 'Försök spara igen'}
          </Knapp>

          {last && !sparar && (
            <Knapp
              variant="stor-tyst"
              onClick={() => {
                sattPlan(null);
                sattFel(null);
              }}
            >
              Ändra uppgifterna
            </Knapp>
          )}
          {!last && (
            <Knapp variant="stor-tyst" onClick={() => sattValdId(null)}>
              Avbryt
            </Knapp>
          )}
        </Yta>
      )}

      {arLast && arChef && (
        <Tomtext>
          Prenumerationen är pausad. Kunderna går att läsa och exportera som
          vanligt, men inget nytt kan sparas förrän betalningen är ordnad.
        </Tomtext>
      )}

      <Tomtext>
        ROT-satsen sitter på KUNDEN och ROT-krysset på MOMENTET. Båda måste finnas
        för att en timme ska bära ROT ut på fakturaunderlaget: momentet säger vad
        som ger ROT, kunden med hur mycket.
      </Tomtext>
    </>
  );
};
